import * as SelectPrimitive from "@radix-ui/react-select";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Archive, Download, Loader2, Pencil } from "lucide-react";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

function getFileTypeLabel(typeOfFile: string): string {
  switch (typeOfFile) {
    case "receipt":
      return "Receipt";
    case "receipt-zip":
      return "Transaction Receipts";
    case "tc-62n":
      return "TC-62N Tax Form";
    case "e-585":
      return "E-585 Form";
    case "e-536R":
      return "E-536R Form";
    default:
      return "Document";
  }
}

import { formatCurrency, getPeriodFromDateRange } from "shared";
import type { RouterOutputs } from "@/lib/trpcClient";
import { env } from "../../../../../env";
export const Route = createFileRoute("/_authed/_org/tax/refunds")({
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const { data: redemptions } = useSuspenseQuery(
    trpc.tax.listRedemptions.queryOptions(),
  );

  return (
    <SheetContent>
      <SheetHeader>
        <SheetTitle>Sales Tax Refunds</SheetTitle>
        {redemptions.length > 0 && (
          <span className="text-muted-foreground text-sm">
            {redemptions.length} refund
            {redemptions.length !== 1 ? "s" : ""} created
          </span>
        )}
      </SheetHeader>

      <div className="mx-2 mt-6">
        <div className="max-h-[calc(100vh-200px)] space-y-4 overflow-y-auto pr-2">
          {redemptions.length === 0 ? (
            <div className="py-8 text-center">
              <Archive className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No redemptions yet</p>
              <p className="text-muted-foreground text-sm">
                Your sales tax redemptions will appear here
              </p>
            </div>
          ) : (
            redemptions.map((redemption) => (
              <RedemptionView
                key={redemption.salesTaxRedemption.id}
                redemption={redemption}
              />
            ))
          )}
        </div>
      </div>
    </SheetContent>
  );
}

type Redemption = RouterOutputs["tax"]["listRedemptions"][number];
function RedemptionView({
  redemption: { salesTaxRedemption, files },
}: {
  redemption: Redemption;
}) {
  const { trpc } = Route.useRouteContext();
  const { mutate, variables, isPending } = useMutation(
    trpc.tax.updateRedemptionStatus.mutationOptions(),
  );
  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      key={salesTaxRedemption.id}
    >
      <div className="flex justify-between">
        <div className="space-y-1">
          <div className="font-semibold">
            {getPeriodFromDateRange(
              salesTaxRedemption.from,
              salesTaxRedemption.to,
            )}
          </div>
          <div className="space-y-1 text-muted-foreground text-sm">
            <span>{salesTaxRedemption.redeemedCount || 0} transactions</span>
            {files.length > 0 && (
              <div className="flex flex-row">
                {files.map((file) => (
                  <a
                    className="flex cursor-pointer items-center rounded-md px-2 py-1 hover:bg-gray-100"
                    download={file.name}
                    href={`${env.VITE_SERVER_BASE_URL}/api/file/${file.id}/download`}
                    key={file.id}
                  >
                    <Download className="mr-1 h-3 w-3 text-gray-600" />
                    <span className="text-xs">
                      {getFileTypeLabel(file.typeOfFile || "receipt")}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1 text-right">
          <div className="font-semibold text-xl">
            {formatCurrency(salesTaxRedemption.redeemedCents)}
          </div>
          <Select
            disabled={isPending}
            onValueChange={(value: "started" | "submitted" | "received") =>
              mutate({
                redemptionId: salesTaxRedemption.id,
                status: value,
              })
            }
            value={variables?.status ?? salesTaxRedemption.status}
          >
            <SelectPrimitive.Trigger className="flex h-6 flex-row items-center space-x-2">
              <RenderStatusBadge status={salesTaxRedemption.status} />
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Pencil className="size-4" />
              )}
            </SelectPrimitive.Trigger>

            <SelectContent>
              <SelectItem value="started">Started</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="received">Received</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
